import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, Check, Lock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const LOGO_URL = `/api/static/images/web/logo_mbs.png`;

const inputCls =
  "mt-2 bg-white border-[#BFBFBF]/70 h-11 text-[#1F3535] placeholder:text-[#6B6B6B]/45 rounded-xl focus-visible:ring-2 focus-visible:ring-[#D4AF37]/40 focus-visible:border-[#1F3535] transition-shadow font-body";
const labelCls = "text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] font-semibold nav-text";
const primaryBtn =
  "w-full bg-[#1F3535] text-white hover:bg-[#0F2424] h-11 rounded-full font-semibold tracking-[0.16em] uppercase text-[11px] shadow-md shadow-[#1F3535]/25 hover:shadow-lg hover:-translate-y-px transition-all";

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#FBF9F5] text-[#333333] flex items-center justify-center px-4 py-10 relative overflow-hidden">
      <div aria-hidden="true" className="absolute inset-0 bg-tech-grid opacity-60" />
      <div aria-hidden="true" className="absolute -top-40 -left-32 w-[420px] h-[420px] rounded-full bg-[radial-gradient(circle,rgba(196,106,74,0.08),transparent_60%)]" />
      <div aria-hidden="true" className="absolute -bottom-40 -right-32 w-[420px] h-[420px] rounded-full bg-[radial-gradient(circle,rgba(0,140,168,0.08),transparent_60%)]" />
      <div className="relative w-full max-w-sm">{children}</div>
    </div>
  );
}

function Header() {
  return (
    <div className="text-center mb-10">
      <Link to="/" className="inline-flex items-center gap-2.5">
        <img src={LOGO_URL} alt="MonBeauSite" className="h-12 w-12 object-contain rounded-xl ring-1 ring-[#D4AF37]/25 shadow-sm bg-white" />
        <span className="text-[17px] font-semibold text-[#1F3535] tracking-tight font-body">
          MonBeauSite<span className="text-[#C46A4A]">.</span>com
        </span>
      </Link>
      <div className="mt-5 flex items-center justify-center gap-2.5" aria-hidden="true">
        <span className="h-px w-10 bg-gradient-to-r from-transparent to-[#D4AF37]/50" />
        <span className="w-1 h-1 rounded-full bg-[#D4AF37]/60" />
        <span className="h-px w-10 bg-gradient-to-l from-transparent to-[#D4AF37]/50" />
      </div>
    </div>
  );
}

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleReset = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }
    if (password !== confirm) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Mot de passe réinitialisé !");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <Shell>
        <Header />
        <div className="bg-white/90 backdrop-blur-sm border border-[#BFBFBF]/45 rounded-2xl px-7 py-10 shadow-sm text-center">
          <p className="text-sm text-[#555555] font-body mb-5">Lien invalide ou expiré.</p>
          <Link to="/login" className="inline-flex items-center gap-1.5 text-[11px] text-[#6B6B6B] hover:text-[#C46A4A] transition-colors font-body">
            <ArrowLeft className="w-3.5 h-3.5" /> Retour à la connexion
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <Header />
      <div className="bg-white/90 backdrop-blur-sm border border-[#BFBFBF]/45 rounded-2xl px-7 py-8 shadow-sm shadow-[#1F3535]/[0.04]">
        {!done ? (
          <form onSubmit={handleReset} className="space-y-5" data-testid="reset-password-form">
            <div className="text-center mb-3">
              <div className="w-12 h-12 rounded-full bg-[#C46A4A]/10 border border-[#C46A4A]/20 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-5 h-5 text-[#C46A4A]" strokeWidth={1.6} />
              </div>
              <h2 className="text-xl font-medium text-[#1F3535] font-montserrat tracking-tight">
                Nouveau <span className="font-playfair italic text-[#C46A4A] font-normal">mot de passe</span>
              </h2>
              <p className="text-xs text-[#6B6B6B] mt-3 leading-relaxed font-body font-light">
                Choisissez un nouveau mot de passe sécurisé (8 caractères minimum).
              </p>
            </div>
            <div>
              <Label className={labelCls}>Nouveau mot de passe</Label>
              <Input data-testid="reset-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="Minimum 8 caractères" required />
            </div>
            <div>
              <Label className={labelCls}>Confirmer le mot de passe</Label>
              <Input data-testid="reset-confirm-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} placeholder="Confirmez votre mot de passe" required />
            </div>
            <Button data-testid="reset-submit-btn" type="submit" disabled={loading} className={primaryBtn}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Réinitialiser"}
            </Button>
          </form>
        ) : (
          <div className="text-center space-y-5 py-2" data-testid="reset-success">
            <div className="w-14 h-14 rounded-full bg-[#1F3535]/[0.06] border border-[#D4AF37]/30 flex items-center justify-center mx-auto">
              <Check className="w-6 h-6 text-[#1F3535]" strokeWidth={2} />
            </div>
            <h2 className="text-xl font-medium text-[#1F3535] font-montserrat tracking-tight">
              Mot de passe <span className="font-playfair italic text-[#C46A4A] font-normal">réinitialisé</span>
            </h2>
            <p className="text-sm text-[#555555] font-body font-light leading-relaxed">
              Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
            </p>
            <Link to="/login">
              <Button className="bg-[#1F3535] text-white hover:bg-[#0F2424] h-11 rounded-full font-semibold tracking-[0.16em] uppercase text-[11px] px-8 shadow-md shadow-[#1F3535]/25" data-testid="go-to-login-btn">
                Se connecter
              </Button>
            </Link>
          </div>
        )}
      </div>

      <div className="mt-8 text-center">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-[11px] text-[#6B6B6B] hover:text-[#C46A4A] transition-colors font-body">
          <ArrowLeft className="w-3.5 h-3.5" /> Retour à la connexion
        </Link>
      </div>
    </Shell>
  );
}
